import { Component, Input, OnInit } from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { CallArrivalService } from './call-arrival.service';
import { getCallArrivalStateColor, getCallArrivalBorderStateColor } from './call-arrival.models';
// import 'chartjs-adapter-moment'; // needed if we revert to timeseries

@Component({
    selector: 'cv-call-arrival',
    templateUrl: './call-arrival.component.html',
    styleUrls: ['./call-arrival.component.scss']
})
export class CallArrivalComponent implements OnInit {



    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    localization: Map<string, string>;

    firstLoad = true;
    data = null;
    dataLoading = false;
    dataLoadSuccess = false;
    canvas = null;

    _animateUpdate = true;
    _datasetBase = {
        xAxisID: "x",
        yAxisID: "y",
        borderWidth: 2,
        pointRadius: 0,
        hitRadius: 4,
    };

    // Chart.js Setup
    _chartConstants = {
        fontColor: "#353a40", // theUpsideDown color
        fontFamily: "'Lato-Regular', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif", // Includes fallbacks (from default ChartJS config)
    };

    chartConfig = {
        type: "line",
        data: {
            datasets: [],
            labels: [],
        },
        options: {
            maintainAspectRatio: false,
            responsive: true,
            datasets: {
                line: {
                    spanGaps: false,
                },
            },
            elements: {
                line: {
                    tension: 0.3
                },
                point: {
                    hitRadius: 4,
                    hoverRadius: 4,
                    radius: 0,
                },
            },
            hover: {
                intersect: true,
                mode: "index",
            },
            layout: {
                padding: {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                },
            },
            plugins: {
                emptyChart: {
                    disableCxone: this.config.isCxOne
                },
                title: {
                    display: false,
                    text: "",
                },
                legend: {
                    display: false,
                    position: "top",
                    labels: {
                        boxWidth: 16,
                        color: this._chartConstants.fontColor,
                        font: {
                            family: this._chartConstants.fontFamily,
                        }
                    },
                },
                tooltip: {
                    intersect: false,
                    mode: "index",
                    yAlign: "bottom",
                    position: "nearest",
                    titleFont: {
                        size: 14,
                    },
                    bodyFont: {
                        size: 14,
                    },
                    callbacks: {
                        // tooltipItem is an array of all Y-values for the given X-value/position being hovered over.
                        // tooltipItem should always have something at index [0] regardless of how many are in the series.
                        // So, get the series-data index of tooltipItem[0], which is used to pull the right x-value label.
                        title: (tooltipItem) => tooltipItem[0].label,
                        label: (tooltipItem) => (tooltipItem.dataset.label + ": " +
                            tooltipItem.dataset.data[tooltipItem.dataIndex]),
                        labelColor: tooltipItem => ({
                            borderColor: tooltipItem.dataset.borderColor,
                            backgroundColor: tooltipItem.dataset.backgroundColor,
                        }),
                    }
                },
            },
            scales: {
                x: {
                    display: true,
                    // type: "timeseries", //fix may need help, wasnt updating chart with this enabled, seems to work without? - Cade Rasmussen
                    title: {
                        display: false,
                        color: this._chartConstants.fontColor,
                        font: {
                            family: this._chartConstants.fontFamily,
                        },
                    },
                    ticks: {
                        autoSkip: true,
                        source: "labels",
                    },
                },
                y: {
                    display: true,
                    position: "left",
                    beginAtZero: true,
                    stacked: true,
                    min: 0,
                    title: {
                        display: false,
                        color: this._chartConstants.fontColor,
                        font: {
                            family: this._chartConstants.fontFamily,
                        },
                    },
                },
            },
        }
    } as ChartConfiguration;

    constructor(
        private service: CallArrivalService,
        private cvLocalizationService: CVLocalizationService,
        public config: CvConfigurationService,
    ) { }

    ngOnInit() {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        this.localization = await this.cvLocalizationService.GetSection("Module");

        this.moduleManager.setOptions({
            onSettingsApplied: settings => this.loadData(false),
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.CAS,
            onRefresh: isAutoRefresh => { return this.loadData(isAutoRefresh); },
            canRefresh: () => !this.dataLoading,
        });

    }

    async loadData(autoRefresh) {
        this.moduleManager.showLoader = !autoRefresh;
        if (this.dataLoading)
            return;
        this.dataLoading = true;

        const response = await this.service.loadData(this.moduleKey, autoRefresh, this.firstLoad);
        // HTTP Success
        this.data = response;
        this.dataLoading = false;
        this.updateChartData();

        this.firstLoad = false;
        this.dataLoadSuccess = true;
        this.moduleManager.showLoader = false;
        this.moduleManager.setAutoRefreshReady();
    };

    updateChartData() {

        const { AlignColorsToAgentStates } = this.moduleManager.customSettings;

        if (this.chartConfig.data.datasets.length === 0) {
            // First time
            let newDatasets = [
                {
                    // Agents On Call - [ Old Settings - Green ] | [ New Settings - Purple ]
                    label:
                        this.localization != null
                            ? this.localization.get("Agents_On_Call")
                            : "Agents On Contact",
                    data: this.data.agentsOnCall,
                    backgroundColor: getCallArrivalStateColor(AlignColorsToAgentStates.getValue(), "AgentsOnContact"),
                    borderColor: getCallArrivalBorderStateColor(AlignColorsToAgentStates.getValue(), "AgentsOnContact"),
                    fill: "stack",
                },
                {
                    // Agents Waiting - [ Old Settings - Purple ] | [ New Settings - Green ]
                    label:
                        this.localization != null
                            ? this.localization.get("Agents_Waiting")
                            : "Agents Waiting",
                    data: this.data.agentsWaiting,
                    backgroundColor: getCallArrivalStateColor(AlignColorsToAgentStates.getValue(), "AgentsWaiting"),
                    borderColor: getCallArrivalBorderStateColor(AlignColorsToAgentStates.getValue(), "AgentsWaiting"),
                    fill: "stack",
                },
                {
                    // Calls On Hold - Red
                    label:
                        this.localization != null
                            ? this.localization.get("Calls_In_Queue")
                            : "Contacts In Queue",
                    data: this.data.callsOnHold,
                    backgroundColor: getCallArrivalStateColor(AlignColorsToAgentStates.getValue(), "ContactsInQueue"),
                    borderColor: getCallArrivalBorderStateColor(AlignColorsToAgentStates.getValue(), "ContactsInQueue"),
                    fill: "stack",
                },
            ];
            newDatasets = newDatasets.map(ds => Object.assign({}, this._datasetBase, ds));
            this.chartConfig.data.datasets = newDatasets;
            this.chartConfig.data.labels = this.data.xAxisLabels;
        } else {
            // Not first time
            this.chartConfig.data.datasets[0].data = this.data.agentsOnCall;
            this.chartConfig.data.datasets[0].backgroundColor = getCallArrivalStateColor(AlignColorsToAgentStates.getValue(), "AgentsOnContact");
            this.chartConfig.data.datasets[0].borderColor = getCallArrivalBorderStateColor(AlignColorsToAgentStates.getValue(), "AgentsOnContact");

            this.chartConfig.data.datasets[1].data = this.data.agentsWaiting;
            this.chartConfig.data.datasets[1].backgroundColor = getCallArrivalStateColor(AlignColorsToAgentStates.getValue(), "AgentsWaiting");
            this.chartConfig.data.datasets[1].borderColor = getCallArrivalBorderStateColor(AlignColorsToAgentStates.getValue(), "AgentsWaiting");

            this.chartConfig.data.datasets[2].data = this.data.callsOnHold;
            this.chartConfig.data.labels = this.data.xAxisLabels;
        }
        this.chartConfig.options.plugins.legend.display = true;
        if (this.data.agentsOnCall.length + this.data.agentsWaiting.length + this.data.callsOnHold.length === 0) {
            this.chartConfig.options.plugins.legend.display = false;
        }
        this._animateUpdate = false; // only animate the first time.
    }

}
